import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminListWaitlist, api, API } from "@/lib/api";
import { Loader2, Download, LogOut, RefreshCw, Copy } from "lucide-react";
import { toast } from "sonner";

const STORAGE_KEY = "mls_admin_key";

export default function AdminWaitlist() {
  const [key, setKey] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [pending, setPending] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authed, setAuthed] = useState(false);

  const load = async (k = key) => {
    if (!k) return;
    setLoading(true);
    try {
      const data = await adminListWaitlist(k);
      setRows(data);
      setAuthed(true);
    } catch (e) {
      setAuthed(false);
      toast.error(e?.response?.data?.detail || "Access denied");
      localStorage.removeItem(STORAGE_KEY);
      setKey("");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (key) load(key);
  }, []);

  const onSubmitKey = (e) => {
    e.preventDefault();
    const k = pending.trim();
    if (!k) return;
    localStorage.setItem(STORAGE_KEY, k);
    setKey(k);
    setPending("");
    load(k);
  };

  const onLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setKey("");
    setAuthed(false);
    setRows([]);
  };

  const onDownload = async () => {
    try {
      const r = await api.get("/admin/waitlist.csv", {
        headers: { "X-Admin-Key": key },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded.");
    } catch {
      toast.error("Download failed");
    }
  };

  const onCopyEmails = () => {
    const list = rows.map((r) => r.email).join(", ");
    navigator.clipboard.writeText(list);
    toast.success(`Copied ${rows.length} emails`);
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-[#EDEDED] grain flex items-center justify-center px-4">
        <form onSubmit={onSubmitKey} className="w-full max-w-md border border-[#222] p-8" data-testid="admin-login">
          <div className="uppercase-mono text-[#8a8a8a]">Admin access</div>
          <h1 className="font-serif text-3xl mt-2">Enter admin key</h1>
          <p className="text-sm text-[#8a8a8a] mt-3 leading-relaxed">
            The key was generated at deploy time. Paste it below to view waitlist signups.
          </p>
          <input
            type="password"
            value={pending}
            onChange={(e) => setPending(e.target.value)}
            placeholder="admin key"
            className="w-full px-3 py-3 mt-6 font-mono text-sm"
            data-testid="admin-key-input"
            autoFocus
          />
          <button
            type="submit"
            className="btn-accent w-full mt-4 py-3 uppercase tracking-widest text-[12px]"
            data-testid="admin-key-submit"
          >
            unlock
          </button>
          <Link
            to="/"
            className="uppercase-mono text-[#555] hover:text-[#CCFF00] block mt-6 text-center"
          >
            ← back to landing
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#EDEDED] grain">
      {/* Top bar */}
      <header className="border-b border-[#222] px-6 lg:px-12 py-6 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="uppercase-mono text-[#8a8a8a] flex items-center gap-3">
            <span>Admin · waitlist</span>
            <Link to="/admin/messages" className="text-[#555] hover:text-[#CCFF00]">
              → messages
            </Link>
          </div>
          <h1 className="font-serif text-3xl lg:text-4xl mt-1">
            {rows.length} <span className="text-[#8a8a8a]">signup{rows.length === 1 ? "" : "s"}</span>
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => load()}
            data-testid="admin-refresh-btn"
            className="btn-ghost px-3 py-2 uppercase tracking-widest text-[11px] inline-flex items-center gap-2"
          >
            <RefreshCw size={12} strokeWidth={1.5} /> refresh
          </button>
          <button
            onClick={onCopyEmails}
            disabled={!rows.length}
            data-testid="admin-copy-emails-btn"
            className="btn-ghost px-3 py-2 uppercase tracking-widest text-[11px] inline-flex items-center gap-2 disabled:opacity-40"
          >
            <Copy size={12} strokeWidth={1.5} /> copy emails
          </button>
          <button
            onClick={onDownload}
            disabled={!rows.length}
            data-testid="admin-csv-btn"
            className="btn-accent px-4 py-2 uppercase tracking-widest text-[11px] inline-flex items-center gap-2 disabled:opacity-40"
          >
            <Download size={12} strokeWidth={1.5} /> export csv
          </button>
          <button
            onClick={onLogout}
            data-testid="admin-logout-btn"
            className="uppercase-mono text-[#8a8a8a] hover:text-[#FF3333] inline-flex items-center gap-1 ml-2"
          >
            <LogOut size={12} strokeWidth={1.5} /> logout
          </button>
        </div>
      </header>

      {/* Table */}
      <section className="px-6 lg:px-12 py-8">
        {loading ? (
          <div className="text-[#8a8a8a] flex items-center gap-2">
            <Loader2 size={16} className="animate-spin" /> loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="border border-dashed border-[#222] p-16 text-center">
            <div className="font-serif text-3xl text-[#8a8a8a]">No signups yet.</div>
            <div className="uppercase-mono text-[#555] mt-2">
              Share <span className="text-[#CCFF00]">/welcome</span> to start collecting
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto border border-[#222]" data-testid="admin-waitlist-table">
            <table className="w-full text-sm">
              <thead className="bg-[#101010] uppercase-mono text-[#8a8a8a]">
                <tr>
                  <th className="text-left px-4 py-3 border-b border-[#222] w-8">#</th>
                  <th className="text-left px-4 py-3 border-b border-[#222]">Email</th>
                  <th className="text-left px-4 py-3 border-b border-[#222]">Role</th>
                  <th className="text-left px-4 py-3 border-b border-[#222]">Tier</th>
                  <th className="text-left px-4 py-3 border-b border-[#222]">Signed up</th>
                  <th className="text-left px-4 py-3 border-b border-[#222]">Referrer</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id} className="hover:bg-[#101010] transition-colors" data-testid={`admin-row-${i}`}>
                    <td className="px-4 py-3 border-b border-[#222] text-[#555] font-mono">
                      {String(rows.length - i).padStart(3, "0")}
                    </td>
                    <td className="px-4 py-3 border-b border-[#222] font-mono">
                      <a
                        href={`mailto:${r.email}`}
                        className="text-[#ededed] hover:text-[#CCFF00]"
                      >
                        {r.email}
                      </a>
                    </td>
                    <td className="px-4 py-3 border-b border-[#222] text-[#ededed]">
                      {r.role || <span className="text-[#555]">—</span>}
                    </td>
                    <td className="px-4 py-3 border-b border-[#222]">
                      {r.tier_interest ? (
                        <span className="text-[10px] uppercase tracking-widest border border-[#CCFF00] text-[#CCFF00] px-2 py-0.5">
                          {r.tier_interest}
                        </span>
                      ) : (
                        <span className="text-[#555]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 border-b border-[#222] font-mono text-[#8a8a8a] whitespace-nowrap">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 border-b border-[#222] text-[#8a8a8a] truncate max-w-[240px]">
                      {r.referrer || <span className="text-[#555]">direct</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <footer className="px-6 lg:px-12 py-6 border-t border-[#222] uppercase-mono text-[#555]">
        Signups are also sent to your Gmail as they arrive · endpoint: {API}/admin/waitlist
      </footer>
    </div>
  );
}
